/* import __COLOCATED_TEMPLATE__ from './subscription-builder.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, keepLatestTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import moment from 'moment-timezone';
import {
  BILLING_PERIODS,
  FIN_AI_COPILOT_BASE_ID,
  AI_INSIGHTS_BASE_ID,
  PLAN_DATA,
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
} from 'embercom/lib/billing';
import type IntlService from 'embercom/services/intl';
import { DEFAULT_REQUEST_PARAMS } from 'embercom/services/quote-service';
// @ts-ignore
import { globalRef } from 'ember-ref-bucket';

import { type TaskGenerator } from 'ember-concurrency';
import type Plan from 'embercom/models/plan';
import type QuoteService from 'embercom/services/quote-service';

interface Args {
  contract: any;
  openSection: string;
  featureName: string;
  planId: number;
  additionalCoreSeatsNumber: number | null;
  newSettings?: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export enum ModalTypes {
  AddingTooManyCopilotSeats = 'adding-too-many-copilot-seats',
  Update = 'update',
  CancelDowngrade = 'cancel-downgrade',
  CannotEditWithScheduledDowngrade = 'cannot-edit-with-scheduled-downgrade',
}

export interface SubscriptionSummary {
  billingPeriod: BILLING_PERIODS;
  selectedPlanId: number;
  selectedPlanName: string;
  contractedSeats: number;
  overageSeats: number;
  contractedCopilotSeats: number;
  overageCopilotSeats: number;
  addOnIds: number[];
}

interface UsageAgreement {
  core_seats: number;
  copilot_seats: number;
}

const FIN_AI_COPILOT_BASE_ID_NUM = Number(FIN_AI_COPILOT_BASE_ID);
const PROACTIVE_SUPPORT_PLUS_BASE_ID_NUM = Number(PROACTIVE_SUPPORT_PLUS_BASE_ID);
const AI_INSIGHTS_BASE_ID_NUM = Number(AI_INSIGHTS_BASE_ID);

const ADDON_DISPLAY_ORDER = [AI_INSIGHTS_BASE_ID, PROACTIVE_SUPPORT_PLUS_BASE_ID];

export default class SubscriptionBuilder extends Component<Signature> {
  @service declare appService: AppService;
  @service declare purchaseAnalyticsService: any;
  @service declare quoteService: QuoteService;
  @service declare intl: IntlService;
  @service declare modelDataCacheService: any;
  @service declare notificationsService: any;
  @service declare customerService: any;
  @service declare store: Store;
  @service declare annualSubscriptionUpdateService: any;
  @service declare router: any;
  @tracked openSectionId = this.args.openSection ?? 'plans';
  @tracked selectedPlanId = this.inSubscriptionPlan.idAsNumber;
  @tracked addOnIdsToAdd: Array<number> = [...this.currentAddonIds].compact() || [];
  @tracked seatsToAddToContract = this.args.additionalCoreSeatsNumber ?? 0;
  @tracked copilotSeatsToAddToContract = 0;
  @tracked seatNumber = this.currentContractedSeats;
  @tracked copilotSeatNumber = this.currentContractedCopilotSeats;
  @tracked currentTotalWithMonthlyOverages = 0;
  @tracked predictedTotalWithMonthlyOverages = 0;
  @tracked currentContractedTotal = 0;
  @tracked predictedContractedTotal = 0;
  @tracked contractedPriceEstimate = '';
  @tracked modalType: ModalTypes | undefined;
  @tracked formattedAnnualTotalOnRenewal = '';
  @globalRef('subscription-management-confetti-canvas') declare confettiCanvas?: HTMLCanvasElement;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    taskFor(this.getQuote).perform();

    // highlight plan if feature and planid is passed in params
    let highlightPlan = this.app.allPlansOnPricingModel.find(
      (plan: Plan) => plan.idAsNumber === Number(this.args.planId),
    );
    if (highlightPlan) {
      if (!highlightPlan.product.addon) {
        this.selectedPlanId = Number(this.args.planId);
      } else {
        this.addOnIdsToAdd = [...this.addOnIdsToAdd, Number(this.args.planId)];
      }
    }
  }

  private resetAllTrackedProperties() {
    this.openSectionId = 'plans';
    this.selectedPlanId = this.inSubscriptionPlan.idAsNumber;
    this.addOnIdsToAdd = [...this.currentAddonIds].compact() || [];
    this.seatsToAddToContract = 0;
    this.copilotSeatsToAddToContract = 0;
    this.seatNumber = this.currentContractedSeats;
    this.copilotSeatNumber = this.currentContractedCopilotSeats;
    this.currentTotalWithMonthlyOverages = 0;
    this.predictedTotalWithMonthlyOverages = 0;
    this.currentContractedTotal = 0;
    this.predictedContractedTotal = 0;
    this.contractedPriceEstimate = '';
    this.modalType = undefined;
    this.formattedAnnualTotalOnRenewal = '';
  }

  @action
  setCurrentTotalWithMonthlyOverages(value: number) {
    this.currentTotalWithMonthlyOverages = value;
  }

  @action
  setPredictedTotalWithMonthlyOverages(value: number) {
    this.predictedTotalWithMonthlyOverages = value;
  }

  @action
  setCurrentContractedTotal(value: number) {
    this.currentContractedTotal = value;
  }

  @action
  setPredictedContractedTotal(value: number) {
    this.predictedContractedTotal = value;
  }

  @action
  setContractedPriceEstimate(value: string) {
    this.contractedPriceEstimate = value;
  }

  @action
  setFormattedAnnualTotalOnRenewal(value: string) {
    this.formattedAnnualTotalOnRenewal = value;
  }

  get activePlanIdsOnRenewal(): number[] {
    return this.customerService.customer.activePlanIdsOnRenewal;
  }

  get usageAgreementOnRenewal(): UsageAgreement | null {
    return this.customerService.customer.usageAgreementOnRenewal;
  }

  get billingPeriod() {
    return BILLING_PERIODS.Annual;
  }

  get loading() {
    return this.quoteService.loading;
  }

  get shouldDisableInputs() {
    return this.loading || this.isReloadingAppAndCustomer || this.isDowngradeScheduled;
  }

  get canSubmit() {
    return this.subscriptionUpdated && !this.shouldDisableInputs;
  }

  get renewalDate() {
    return this.customerService.selfServeRenewalDate;
  }

  get endDate() {
    return moment(this.renewalDate).subtract(1, 'day');
  }

  get formattedRenewalDate() {
    return moment(this.renewalDate).format('ll');
  }

  get app() {
    return this.appService.app;
  }

  get selectedPlan(): Plan | undefined {
    return this.app.allPlansOnPricingModel.find(
      (plan: Plan) => plan.idAsNumber === this.selectedPlanId,
    );
  }

  get selectedPlanName() {
    return this.intl.t(this.selectedPlan?.nameTranslationKey);
  }

  // seats
  get adminsWithCoreSeats() {
    return this.quoteService.fullSeatCount;
  }

  get adminsWithCopilotSeats() {
    return this.quoteService.copilotSeatCount;
  }

  get adminsWithLiteSeats() {
    return this.quoteService.liteSeatCount;
  }

  private getPlanLiteSeatMapping(planId: number) {
    return PLAN_DATA[planId].liteSeatLimit ?? 0;
  }

  get newOverageSeats() {
    return this.calculateSeatOverage(this.newContractedSeats, this.adminsWithCoreSeats);
  }

  get newOverageCopilotSeats() {
    if (this.copilotIsOnNonGraduatingTrial) {
      return 0;
    }
    return this.calculateSeatOverage(this.newContractedCopilotSeats, this.adminsWithCopilotSeats);
  }

  private calculateSeatOverage(currentContractedSeats: number, adminWithSeat: number) {
    return currentContractedSeats < adminWithSeat ? adminWithSeat - currentContractedSeats : 0;
  }

  get currentOverageSeats() {
    return this.calculateSeatOverage(this.currentContractedSeats, this.adminsWithCoreSeats);
  }

  get copilotPlan() {
    return this.app.allPlansOnPricingModel.find(
      (plan: Plan) => plan.idAsNumber === FIN_AI_COPILOT_BASE_ID_NUM,
    );
  }

  get copilotPlanName() {
    return this.intl.t(this.copilotPlan.nameTranslationKey);
  }

  get copilotIsOnNonGraduatingTrial() {
    return this.copilotPlan.activeTrial && !this.copilotPlan.activeTrialIsGraduating;
  }

  get showCopilotContractedSeatSelector() {
    if (!this.copilotIsOnNonGraduatingTrial) {
      return true;
    }
    return this.copilotSeatsToAddToContract > 0;
  }

  get currentCopilotOverageSeats() {
    if (this.copilotIsOnNonGraduatingTrial) {
      return 0;
    }
    return this.calculateSeatOverage(
      this.currentContractedCopilotSeats,
      this.adminsWithCopilotSeats,
    );
  }

  private calculateContractedSeats(currentContractedSeats: number, seatsToAddToContract: number) {
    return currentContractedSeats + seatsToAddToContract;
  }

  get newContractedSeats() {
    return this.calculateContractedSeats(this.currentContractedSeats, this.seatsToAddToContract);
  }

  get newContractedCopilotSeats() {
    return this.calculateContractedSeats(
      this.currentContractedCopilotSeats,
      this.copilotSeatsToAddToContract,
    );
  }

  get currentContractedSeats(): number {
    return this.args.contract.contractUsageLimits.core_seat_count;
  }

  get minimumContractedSeats(): number {
    return 1;
  }

  get selectedCopilotAddon() {
    return (
      this.copilotSeatsToAddToContract > 0 &&
      !this.addOnIdsToAdd.includes(FIN_AI_COPILOT_BASE_ID_NUM)
    );
  }

  get currentContractedCopilotSeats(): number {
    return this.args.contract.contractUsageLimits.copilot_seat_count || 0;
  }

  get minimumContractedCopilotSeats(): number {
    return 0;
  }

  // plans
  get inSubscriptionPlan() {
    return this.app.allPlansOnPricingModel.find(
      (plan: Plan) => plan.billableCustomerPlan && !plan.product.addon,
    );
  }

  get inSubscriptionPlanName() {
    return this.intl.t(this.inSubscriptionPlan.nameTranslationKey);
  }

  get availablePlans() {
    return this.app.allPlansOnPricingModel.filter(
      (plan: Plan) => !plan.product.addon && plan.selfServe,
    );
  }

  get currentAddonIds(): number[] {
    return (
      this.app.allPlansOnPricingModel
        .filter((plan: Plan) => plan.billableCustomerPlan && plan.product.addon)
        .map((plan: Plan) => plan.idAsNumber) || []
    );
  }

  get availableAddons() {
    let allAddons = this.app.allPlansOnPricingModel.filter(
      (plan: Plan) =>
        plan.product.addon &&
        plan.selfServe &&
        plan.id !== FIN_AI_COPILOT_BASE_ID &&
        (plan.id !== AI_INSIGHTS_BASE_ID || this.app.canSeeAiInsightsAddon),
    );

    let orderedAddons: Plan[] = [];

    for (let addonId of ADDON_DISPLAY_ORDER) {
      let addon = allAddons.find((plan: Plan) => plan.id === addonId);
      if (addon) {
        orderedAddons.push(addon);
      }
    }

    let remainingAddons = allAddons.filter((plan: Plan) => !ADDON_DISPLAY_ORDER.includes(plan.id));
    orderedAddons.push(...remainingAddons);

    return orderedAddons;
  }

  get extraLiteSeatsForDowngradePlan() {
    if (this.isDowngrading) {
      return this.adminsWithLiteSeats - this.getPlanLiteSeatMapping(this.selectedPlanId);
    }
    if (this.scheduledDowngradeCorePlanId) {
      return (
        this.adminsWithLiteSeats - this.getPlanLiteSeatMapping(this.scheduledDowngradeCorePlanId)
      );
    }
    return 0;
  }

  get currentSubscriptionSummary(): SubscriptionSummary {
    return {
      billingPeriod: this.billingPeriod,
      selectedPlanId: this.inSubscriptionPlan.idAsNumber,
      selectedPlanName: this.inSubscriptionPlanName,
      contractedSeats: this.currentContractedSeats,
      overageSeats: this.currentOverageSeats,
      contractedCopilotSeats: this.currentContractedCopilotSeats,
      overageCopilotSeats: this.currentCopilotOverageSeats,
      addOnIds: this.currentAddonIds,
    };
  }

  get updatedSubscriptionSummary(): SubscriptionSummary {
    return {
      billingPeriod: this.billingPeriod,
      selectedPlanId: this.selectedPlanId,
      selectedPlanName: this.selectedPlanName,
      contractedSeats: this.newContractedSeats,
      overageSeats: this.newOverageSeats,
      contractedCopilotSeats: this.newContractedCopilotSeats,
      overageCopilotSeats: this.newOverageCopilotSeats,
      addOnIds: this.addOnIdsToAdd,
    };
  }

  get upgradeSummaryPlan(): Plan | undefined {
    let tierIds = [this.inSubscriptionPlan.tierId, this.selectedPlan?.tierId];
    let maxTierId = Math.max(...tierIds);
    return this.app.allPlansOnPricingModel.find((plan: Plan) => plan.tierId === maxTierId);
  }

  get upgradeSummaryPlanId() {
    return Number(this.upgradeSummaryPlan?.idAsNumber);
  }

  get upgradeSummaryPlanName() {
    return this.intl.t(this.upgradeSummaryPlan?.nameTranslationKey);
  }

  get upgradeSummaryAddOnIds() {
    return Array.from(new Set([...this.currentAddonIds, ...this.addOnIdsToAdd]));
  }

  get upgradeSummaryContractedSeats() {
    return Math.max(this.currentContractedSeats, this.newContractedSeats);
  }

  get upgradeSummaryOverageSeats() {
    return Math.max(this.currentOverageSeats, this.newOverageSeats);
  }

  get upgradeSummaryContractedCopilotSeat() {
    return Math.max(this.currentContractedCopilotSeats, this.newContractedCopilotSeats);
  }

  get upgradeSummaryOverageCopilotSeats() {
    return Math.max(this.currentCopilotOverageSeats, this.newOverageCopilotSeats);
  }

  get upgradedSubscriptionSummary() {
    return {
      billingPeriod: this.billingPeriod,
      selectedPlanId: this.upgradeSummaryPlanId,
      selectedPlanName: this.upgradeSummaryPlanName,
      contractedSeats: this.upgradeSummaryContractedSeats,
      overageSeats: this.upgradeSummaryOverageSeats,
      contractedCopilotSeats: this.upgradeSummaryContractedCopilotSeat,
      overageCopilotSeats: this.upgradeSummaryOverageCopilotSeats,
      addOnIds: this.upgradeSummaryAddOnIds,
    };
  }

  get scheduledDowngradePlans(): Plan[] {
    return this.app.allPlansOnPricingModel.filter((plan: Plan) =>
      this.activePlanIdsOnRenewal.includes(plan.idAsNumber),
    );
  }

  get scheduledDowngradeCorePlan(): Plan | undefined {
    return this.scheduledDowngradePlans.find((plan: Plan) => !plan.product.addon);
  }

  get scheduledDowngradeCorePlanId() {
    return this.scheduledDowngradeCorePlan?.idAsNumber;
  }

  get scheduledDowngradeCorePlanName() {
    return this.scheduledDowngradeCorePlan
      ? this.intl.t(this.scheduledDowngradeCorePlan.nameTranslationKey)
      : undefined;
  }

  get scheduledDowngradeCoreSeats() {
    return this.usageAgreementOnRenewal?.['core_seats'];
  }

  get scheduledDowngradeCopilotSeats() {
    return this.usageAgreementOnRenewal?.['copilot_seats'];
  }

  get scheduledDowngradeAddonIds() {
    return this.scheduledDowngradePlans
      .filter((plan: Plan) => plan.product.addon)
      .map((plan: Plan) => plan.idAsNumber);
  }

  get scheduledDowngradeSubscriptionSummary() {
    if (
      this.scheduledDowngradeCorePlanId === undefined ||
      this.scheduledDowngradeCorePlanName === undefined ||
      this.scheduledDowngradeCoreSeats === undefined ||
      this.scheduledDowngradeCopilotSeats === undefined ||
      !this.isDowngradeScheduled
    ) {
      return undefined;
    }
    return {
      billingPeriod: this.billingPeriod,
      selectedPlanId: this.scheduledDowngradeCorePlanId,
      selectedPlanName: this.scheduledDowngradeCorePlanName,
      contractedSeats: this.scheduledDowngradeCoreSeats,
      overageSeats: 0,
      contractedCopilotSeats: this.scheduledDowngradeCopilotSeats,
      overageCopilotSeats: 0,
      addOnIds: this.scheduledDowngradeAddonIds,
    };
  }

  get downgradeSubscriptionSummary() {
    if (this.isDowngrading) {
      return this.updatedSubscriptionSummary;
    }
    return this.scheduledDowngradeSubscriptionSummary;
  }

  get isDowngradeScheduled() {
    return this.isPlanDowngradeScheduled || this.isAnnualToMonthlyDowngradeScheduled;
  }

  get isPlanDowngradeScheduled() {
    return !!(
      this.scheduledDowngradeCorePlan &&
      (this.scheduledDowngradeCorePlanId !== this.inSubscriptionPlan.idAsNumber ||
        this.scheduledDowngradeAddonIds.length !== this.currentAddonIds.length ||
        this.scheduledDowngradeCoreSeats !== this.currentContractedSeats ||
        this.scheduledDowngradeCopilotSeats !== this.currentContractedCopilotSeats)
    );
  }

  get isAnnualToMonthlyDowngradeScheduled() {
    return false;
  }

  get dateFormat() {
    return {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
  }

  get isCancellationScheduled() {
    return !!this.customerService.customer.subscriptionWillBeCancelledAt;
  }

  get currentOrderSummaryFooterDateCopy() {
    if (this.isDowngradeScheduled) {
      return this.intl.t('billing.annual-manage-subscription.ends-on', {
        endDate: this.intl.formatDate(this.endDate, this.dateFormat),
      });
    }
    if (this.isCancellationScheduled && !this.subscriptionUpdated) {
      return this.intl.t('billing.annual-manage-subscription.ends-on', {
        endDate: this.intl.formatDate(
          this.customerService.customer.subscriptionWillBeCancelledAt,
          this.dateFormat,
        ),
      });
    }
    return this.intl.t('billing.annual-manage-subscription.renewal-date', {
      renewalDate: this.intl.formatDate(this.renewalDate, this.dateFormat),
    });
  }

  get remainingItemsOnSubscriptionOnCancellingDowngrade() {
    let itemsOnSubscription = [
      this.intl.t('billing.annual-manage-subscription.plan-name', {
        planName: this.inSubscriptionPlanName,
      }),
      this.intl.t('billing.annual-manage-subscription.full-seats', {
        fullSeats: this.currentContractedSeats,
      }),
      this.intl.t('billing.annual-manage-subscription.lite-seats', {
        liteSeatCount: this.getPlanLiteSeatMapping(this.inSubscriptionPlan.idAsNumber),
      }),
    ];

    if (this.currentContractedCopilotSeats > 0) {
      itemsOnSubscription.push(
        this.intl.t('billing.annual-manage-subscription.copilot-seats', {
          copilotSeats: this.currentContractedCopilotSeats,
        }),
      );
    }

    let currentAddOnNames = this.availableAddons
      .filter((addOn: Plan) => this.currentAddonIds.includes(addOn.idAsNumber))
      .map((addOn: Plan) => addOn.name);

    itemsOnSubscription.push(...currentAddOnNames);

    return itemsOnSubscription;
  }

  get subscriptionUpdated() {
    return this.isUpgrading || this.isDowngrading;
  }

  get isUpgrading() {
    return !!(
      this.isUpgradingCorePlan ||
      this.isUpgradingAddOns ||
      this.isUpgradingSeats ||
      this.isUpgradingCopilotSeats
    );
  }

  get isDowngrading() {
    return !!(
      this.isDowngradingCorePlan ||
      this.isDowngradingAddOns ||
      this.isDowngradingSeats ||
      this.isDowngradingCopilotSeats
    );
  }

  get isDowngradingToEssential() {
    return this.isDowngradingCorePlan && this.selectedPlan?.id === PRICING_5_X_CORE_ESSENTIAL_ID;
  }

  private get isUpgradingCorePlan() {
    return (
      this.selectedPlan &&
      this.inSubscriptionPlan &&
      this.selectedPlan.tierId > this.inSubscriptionPlan.tierId
    );
  }

  private get isDowngradingCorePlan() {
    return (
      this.selectedPlan &&
      this.inSubscriptionPlan &&
      this.selectedPlan.tierId < this.inSubscriptionPlan.tierId
    );
  }

  private get isUpgradingAddOns() {
    return this.addOnIdsToAdd.some((addOnId) => !this.currentAddonIds.includes(addOnId));
  }

  private get isDowngradingAddOns() {
    return this.currentAddonIds.some((addOnId) => !this.addOnIdsToAdd.includes(addOnId));
  }

  private get isUpgradingSeats() {
    return this.seatsToAddToContract > 0;
  }

  private get isDowngradingSeats() {
    return this.seatsToAddToContract < 0;
  }

  private get isUpgradingCopilotSeats() {
    return this.copilotSeatsToAddToContract > 0;
  }

  private get isDowngradingCopilotSeats() {
    return this.copilotSeatsToAddToContract < 0;
  }

  private calculateSeatOverageSavings(
    seatPrice: number,
    discountedSeatPrice: number,
    overageSeats: number,
  ) {
    return (seatPrice - discountedSeatPrice) * overageSeats * BILLING_PERIODS.Annual;
  }

  //prices
  get overageSavings() {
    return this.calculateSeatOverageSavings(
      this.fullSeatPrice,
      this.discountedSeatPrice,
      this.newOverageSeats,
    );
  }

  get copilotOverageSavings() {
    return this.calculateSeatOverageSavings(
      this.copilotSeatPrice,
      this.discountedCopilotSeatPrice,
      this.newOverageCopilotSeats,
    );
  }

  get priceDifferenceWithMonthlyOverages() {
    return this.predictedTotalWithMonthlyOverages - this.currentTotalWithMonthlyOverages;
  }

  get formattedPriceDifferenceWithMonthlyOverages() {
    let prefix = this.priceDifferenceWithMonthlyOverages > 0 ? '+' : '';
    return `${prefix}${this.quoteService.formatPrice(this.priceDifferenceWithMonthlyOverages)}`;
  }

  quote(id: number) {
    return this.quoteService.getQuoteById([id]);
  }

  get fullSeatPrice() {
    return this.quote(this.selectedPlanId)?.fullSeatPrice(this.selectedPlanId) || 0;
  }

  get copilotSeatPrice() {
    return (
      this.quote(FIN_AI_COPILOT_BASE_ID_NUM)?.fullSeatPrice?.(
        FIN_AI_COPILOT_BASE_ID_NUM,
        Metric.copilot_seat_count,
      ) ?? 0
    );
  }

  get discountedCopilotSeatPrice() {
    return (
      this.quote(FIN_AI_COPILOT_BASE_ID_NUM)?.discountedAnnualSeatPrice?.(
        FIN_AI_COPILOT_BASE_ID_NUM,
        Metric.copilot_seat_count,
      ) ?? 0
    );
  }

  get discountedSeatPrice() {
    return this.quote(this.selectedPlanId)?.discountedAnnualSeatPrice?.(this.selectedPlanId) || 0;
  }

  get modalHeader() {
    return this.modalType ? this.modalCopy[this.modalType].header : '';
  }

  get modalConfirm() {
    return this.modalType ? this.modalCopy[this.modalType].confirm : '';
  }

  private get modalCopy() {
    return {
      [ModalTypes.Update]: {
        header: this.isUpgrading
          ? this.intl.t('billing.annual-manage-subscription.modal.update.header.confirm-payment')
          : this.intl.t(
              'billing.annual-manage-subscription.modal.update.header.schedule-downgrade',
            ),
        confirm: this.isUpgrading
          ? this.intl.t('billing.annual-manage-subscription.modal.update.confirm.pay-now')
          : this.intl.t(
              'billing.annual-manage-subscription.modal.update.confirm.schedule-downgrade',
            ),
      },
      [ModalTypes.CancelDowngrade]: {
        header: this.intl.t('billing.annual-manage-subscription.modal.cancel-downgrade.header'),
        confirm: this.intl.t('billing.annual-manage-subscription.modal.cancel-downgrade.confirm'),
      },
      [ModalTypes.CannotEditWithScheduledDowngrade]: {
        header: this.intl.t(
          'billing.annual-manage-subscription.modal.cannot-edit-with-scheduled-downgrade.header',
        ),
        confirm: this.intl.t(
          'billing.annual-manage-subscription.modal.cannot-edit-with-scheduled-downgrade.confirm',
        ),
      },
      [ModalTypes.AddingTooManyCopilotSeats]: {
        header: this.intl.t(
          'billing.annual-manage-subscription.modal.adding-too-many-copilot-seats.header',
        ),
        confirm: this.intl.t(
          'billing.annual-manage-subscription.modal.adding-too-many-copilot-seats.confirm',
        ),
      },
    };
  }

  get modalLabel() {
    if (this.isSubmitActionTaskRunning) {
      return this.intl.t(`billing.annual-manage-subscription.just-a-moment`);
    } else {
      return this.modalConfirm;
    }
  }

  get modalConfirmDisabled() {
    return this.isSubmitActionTaskRunning;
  }

  get canCloseModal() {
    return !this.isSubmitActionTaskRunning;
  }

  get submitButtonLabel() {
    if (this.isUpgrading && this.isDowngrading) {
      return this.intl.t('billing.annual-manage-subscription.buy-now-and-schedule-downgrade');
    }
    if (this.isDowngrading) {
      return this.intl.t('billing.annual-manage-subscription.schedule-downgrade');
    }
    if (this.isUpgrading) {
      return this.intl.t('billing.annual-manage-subscription.buy-product-now');
    }
    return '';
  }

  modalEventName(modalType: ModalTypes) {
    let update_object_suffix = '';
    if (modalType === ModalTypes.Update) {
      if (this.isUpgrading && this.isDowngrading) {
        update_object_suffix = '_upgrade_and_downgrade';
      } else if (this.isUpgrading) {
        update_object_suffix = '_upgrade';
      } else if (this.isDowngrading) {
        update_object_suffix = '_downgrade';
      }
    }

    return `modal_${modalType.replace(/-/g, '_')}${update_object_suffix}`;
  }

  @action
  showConfirmationModal() {
    this.showModal(ModalTypes.Update);
  }

  @action
  showModal(modalType: ModalTypes) {
    this.modalType = modalType;
    this.sendAnalyticsEvent(this.modalEventName(modalType), 'opened');
  }

  @action
  showCancelDowngradeModal() {
    this.showModal(ModalTypes.CancelDowngrade);
  }

  @action
  modalAction() {
    switch (this.modalType) {
      case ModalTypes.Update:
        this.sendAnalyticsEvent(this.modalEventName(this.modalType), 'confirm');
        taskFor(this.upgradeNow).perform();
        break;
      case ModalTypes.CancelDowngrade:
      case ModalTypes.CannotEditWithScheduledDowngrade:
        this.sendAnalyticsEvent(this.modalEventName(this.modalType), 'confirm');
        taskFor(this.cancelScheduledDowngrade).perform();
        break;
      default:
        this.closeModal();
        break;
    }
  }

  get displayModal() {
    return this.modalType !== undefined;
  }

  @action
  closeModal() {
    let object = this.modalType ? this.modalEventName(this.modalType) : '';
    let action = 'close';
    this.sendAnalyticsEvent(object, action);
    this.modalType = undefined;
  }

  @action
  updateSeatNumber(seatNumber: number) {
    this.seatNumber = seatNumber;
    this.seatsToAddToContract = this.seatNumber - this.currentContractedSeats;

    if (this.seatNumber < this.copilotSeatNumber) {
      this.copilotSeatNumber = this.seatNumber;
      this.updateCopilotSeatNumber(this.seatNumber);
    }
    this.purchaseAnalyticsService.trackEvent({
      action: 'inputed',
      object: 'seat_number_input',
      context: { seat_number: `${this.seatNumber}` },
      place: 'annual_plans_manage_subscription',
    });
  }

  @action
  updateCopilotSeatNumber(inputtedCopilotSeat: number | string) {
    let inputtedCopilotSeatAsNumber = +inputtedCopilotSeat;

    this.copilotSeatNumber = inputtedCopilotSeatAsNumber;
    this.copilotSeatsToAddToContract = this.copilotSeatNumber - this.currentContractedCopilotSeats;
    this.purchaseAnalyticsService.trackEvent({
      action: 'inputed',
      object: 'copilot_seat_number_input',
      context: { seat_number: `${this.copilotSeatNumber}` },
      place: 'annual_plans_manage_subscription',
    });
  }

  @action
  moveMonthlyOverageSeatsToAnnual() {
    let newSeatNumber = this.seatNumber + this.newOverageSeats;
    this.updateSeatNumber(newSeatNumber);

    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: 'move_overage_seats',
      context: { seat_number: `${newSeatNumber}` },
      place: 'annual_plans_manage_subscription',
    });
  }

  @action
  moveMonthlyOverageCopilotSeatsToAnnual() {
    let newCopilotSeatNumber = this.copilotSeatNumber + this.newOverageCopilotSeats;
    this.updateCopilotSeatNumber(newCopilotSeatNumber);
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: 'move_overage_copilot_seats',
      context: { seat_number: `${newCopilotSeatNumber}` },
      place: 'annual_plans_manage_subscription',
    });
  }

  @action
  moveTrialingCopilotSeats() {
    this.copilotSeatNumber = this.adminsWithCopilotSeats;
    this.copilotSeatsToAddToContract = this.copilotSeatNumber;
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: 'move_trialing_copilot_seats',
      context: { seat_number: `${this.copilotSeatNumber}` },
      place: 'annual_plans_manage_subscription',
    });
  }

  @action setSelected(plan: Plan) {
    // janky AF
    if (!plan.product.addon) {
      if (this.selectedPlanId === plan.idAsNumber) {
        //unset
        this.selectedPlanId = this.inSubscriptionPlan.idAsNumber;
      } else {
        this.selectedPlanId = plan.idAsNumber;
      }
    }
    if (plan.product.addon) {
      if (!this.addOnIdsToAdd?.includes(plan.idAsNumber)) {
        this.addOnIdsToAdd = [...this.addOnIdsToAdd, plan.idAsNumber];
      } else {
        this.addOnIdsToAdd = [...this.addOnIdsToAdd.filter((id) => id !== plan.idAsNumber)];
      }
    }

    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: plan.product.addon ? 'addon' : 'plan',
      context: {
        is_addon: plan.product.addon,
        ...(plan.product.addon
          ? {
              addon_id: `${plan.idAsNumber}`,
              selected_addon_ids: this.addOnIdsToAdd,
            }
          : {
              plan_id: `${plan.idAsNumber}`,
              current_plan_id: `${this.inSubscriptionPlan.idAsNumber}`,
            }),
      },
      place: 'annual_plans_manage_subscription',
    });
  }

  @action sendAnalyticsEvent(object: string, action: string) {
    this.purchaseAnalyticsService.trackEvent({
      action,
      object,
      context: 'update_subscription',
      place: 'annual_plans_manage_subscription',
    });
  }

  @dropTask
  *upgradeNow(): TaskGenerator<void> {
    let upgradePlanIds = [this.selectedPlanId, ...this.addOnIdsToAdd].compact();
    if (this.selectedCopilotAddon) {
      upgradePlanIds.push(FIN_AI_COPILOT_BASE_ID_NUM);
    }

    let eventPayload: {
      current: SubscriptionSummary;
      upgrade?: SubscriptionSummary;
      downgrade?: SubscriptionSummary;
    } = {
      current: { ...this.currentSubscriptionSummary },
      upgrade: this.isUpgrading ? this.upgradedSubscriptionSummary : undefined,
      downgrade: this.isDowngrading ? this.updatedSubscriptionSummary : undefined,
    };

    let action = '';

    if (this.isUpgrading && this.isDowngrading) {
      action = 'upgraded_and_downgraded';
    } else if (this.isUpgrading) {
      action = 'upgraded';
    } else if (this.isDowngrading) {
      action = 'downgraded';
    }

    try {
      yield this.annualSubscriptionUpdateService.update.perform({
        planIds: upgradePlanIds,
        coreSeats: this.newContractedSeats,
        copilotSeats: this.newContractedCopilotSeats,
      });
      taskFor(this.reloadAppAndCustomer).perform();
      this.closeModal();

      this.notificationsService.notifyConfirmation(
        this.intl.t('billing.annual-manage-subscription.upgrade-success'),
      );

      this.purchaseAnalyticsService.trackEvent({
        action,
        object: 'update_button',
        context: 'update_subscription',
        place: 'annual_plans_manage_subscription',
        update_info: eventPayload,
      });
    } catch (error) {
      console.error('Error upgrading subscription', error);
      this.notificationsService.notifyError(error.jqXHR.responseJSON.error);
    }
  }

  @dropTask
  *reloadAppAndCustomer(): TaskGenerator<void> {
    yield this.appService.app.reload();
    yield this.store.findRecord('billing/contract', this.app.id, { reload: true });
    yield this.customerService.resetAll();
    yield this.customerService.refreshCustomer();
    //reset all tracked properties so that on success the page reflects what would show on a fresh load of the page with the new customer state
    this.resetAllTrackedProperties();
  }

  get isReloadingAppAndCustomer() {
    return taskFor(this.reloadAppAndCustomer).isRunning;
  }

  @dropTask
  *cancelScheduledDowngrade() {
    try {
      yield this.annualSubscriptionUpdateService.cancelScheduledDowngrade.perform();
      taskFor(this.reloadAppAndCustomer).perform();
      this.closeModal();

      this.notificationsService.notifyConfirmation(
        this.intl.t('billing.annual-manage-subscription.scheduled-downgrade-cancelled'),
      );

      this.purchaseAnalyticsService.trackEvent({
        action: 'cancelled',
        object: 'scheduled_downgrade',
        context: 'update_subscription',
        place: 'annual_plans_manage_subscription',
      });
    } catch (error) {
      console.error('Error cancelling scheduled downgrade', error);
      this.notificationsService.notifyError(error.jqXHR.responseJSON.error);
    }
  }

  get isSubmitActionTaskRunning() {
    return taskFor(this.cancelScheduledDowngrade).isRunning || taskFor(this.upgradeNow).isRunning;
  }

  @keepLatestTask
  *getQuote() {
    let corePlanIds = this.availablePlans.map((plan: Plan) => plan.idAsNumber);
    let planIds = [
      ...corePlanIds,
      PROACTIVE_SUPPORT_PLUS_BASE_ID_NUM,
      FIN_AI_COPILOT_BASE_ID_NUM,
      AI_INSIGHTS_BASE_ID_NUM,
    ];
    let params = planIds.map((id) => ({
      ...DEFAULT_REQUEST_PARAMS,
      planIds: [id],
      source: 'pricing-five-annual-upgrade',
    }));

    yield taskFor(this.quoteService.getQuotes).perform(params);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::AnnualManageSubscription::SubscriptionBuilder': typeof SubscriptionBuilder;
    'billing/annual-manage-subscription/subscription-builder': typeof SubscriptionBuilder;
  }
}
